<template>
  <div class="content mb-5">
    <b-row>
      <b-col class="text-right mb-4">
        {{ this.$t("Last updated:") }} {{ timeFromLastUpdate }}
      </b-col>
    </b-row>

    <b-row class="mb-5">
      <b-col :class="{ 't-table-busy': true }">
        <b-overlay :show="refreshingData" rounded="sm" :opacity="0.5">
          <t-data-table
            data-test-id="recent-traffic-data-table"
            with-filter
            disable-row-select
            :loading="loading"
            :fetching="fetching"
            :title="this.$t('Most Recent Requests')"
            :filter-placeholder="$t('Search Requests')"
            :items-per-page="100"
            :table-data="dataSet"
            :actions="tableActions"
            user-role="customer"
            @on-add="refreshList"
            @on-export="onExport"
            :searchExports="searchExports"
            @export-headers="exportedHeaders"
          />
        </b-overlay>
      </b-col>
    </b-row>
  </div>
</template>

<route>
{
"name": "Customer Recent Traffic",
"meta": {
 "authorize" : [1, 3, 4]
}
}
</route>

<script>
import { mapActions, mapGetters } from "vuex";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

export default {
  layout: "customer",
  data() {
    return {
      loading: true,
      currentHeaders: null,
      fetching: false,
      today: dayjs().format("YYYY-MM-DD"),
      searchExports: ["user", "url", "location", "externalip"],
      lastUpdated: dayjs().format(),
      timeFromLastUpdate: dayjs(this.lastUpdated).fromNow(),
      action: "all",
      refreshingData: false,
      lastSearch: null,
      selectedRows: [],
      tableActions: {
        add: {
          enabled: true,
          actions: [
            {
              text: this.$t("Refresh"),
              value: true,
            },
          ],
        },
        import: { enabled: false },
        export: { enabled: true },
        delete: { enabled: false },
      },
      dataSet: {
        fields: [
          { value: "time", text: this.$t("Time"), sortable: true },
          { value: "user", text: this.$t("User"), sortable: true },
          { value: "url", text: this.$t("Destination"), sortable: true },
          { value: "action", text: this.$t("Action"), sortable: true },
          { value: "category", text: this.$t("Category"), sortable: true },
          { value: "internalip", text: this.$t("Internal IP"), sortable: true },
          { value: "externalip", text: this.$t("External IP"), sortable: true },
          { value: "location", text: this.$t("Location"), sortable: true },
          {
            value: "policy",
            text: this.$t("Effective Policy"),
            sortable: true,
          },
        ],
        items: [],
      },
      desserts: [],
    };
  },
  computed: {
    ...mapGetters("authentication", ["selectedUser"]),
    ...mapGetters("customerHistory", ["customerHistory"]),
    lastUpdatedAt() {
      return `Last updated: ${this.timeFromLastUpdate}`;
    },
  },
  methods: {
    ...mapActions("pageMeta", ["setPageTitle", "setPageSubtitle"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("customerHistory", ["fetchCustomerHistory"]),
    async refreshList() {
      this.refreshingData = true;
      await this.fetchCustomerHistory({
        id: this.selectedUser.id,
        date: this.today,
        action: this.action,
        limit: this.lastSearch ? this.lastSearch.itemsPerPage : 1000,
        q: this.lastSearch ? this.lastSearch.filter : "",
      });
      this.dataSet.items = this.customerHistory;
      this.lastUpdated = dayjs().format();
      this.createTimeInterval();
      this.refreshingData = false;
    },
    createTimeInterval() {
      if (this.intervalID) clearInterval(this.intervalID);

      this.timeFromLastUpdate = dayjs(this.lastUpdated).fromNow();

      this.intervalID = setInterval(() => {
        this.timeFromLastUpdate = dayjs(this.lastUpdated).fromNow();
      }, 10000);
    },
    exportedHeaders(headers) {
      this.currentHeaders = headers;
    },
    onExport(items) {
      if (items.length) {
        const exportData = items.map((res) => {
          let payload = {};
          this.currentHeaders.forEach((headers) => {
            return (payload[headers.value] = res[headers.value]);
          });

          return payload;
        });
        this.$downloadCSV(exportData, "recenttraffic");
      } else {
        this.displayToast({
          title: this.$t("Failure"),
          message: this.$t("No Recent traffic in table"),
          duration: 2000,
          variant: "danger",
        });
      }
    },
    async onSearch(options) {
      this.fetching = true;
      await this.fetchCustomerHistory({
        id: this.selectedUser.id,
        date: this.today,
        action: this.action,
        limit: options.itemsPerPage,
        q: options.filter,
      });
      this.lastSearch = options;
      this.createTimeInterval();
      this.fetching = false;
    },
  },
  watch: {
    customerHistory() {
      this.dataSet.items = this.customerHistory;
    },
  },
  async created() {
    this.setPageTitle(this.$t("Recent Traffic"));
    this.setPageSubtitle([
      this.$t("View details of your users most recent browsing activity."),
    ]);

    await this.fetchCustomerHistory({
      id: this.selectedUser.id,
      date: this.today,
      action: this.action,
      limit: 1000,
    });
    this.dataSet.items = this.customerHistory;
    this.loading = false;

    this.createTimeInterval();
  },
  destroyed() {
    clearInterval(this.intervalID);
  },
};
</script>

<style scoped lang="scss"></style>
